import * as React from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { cva, VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

type TabsThemeContextType = {
  variant: "default" | "underline";
};

// Theme pattern lets us use the same primitive with different styles.
const TabsThemeContext = React.createContext<TabsThemeContextType>({
  variant: "default",
});

function useTabsTheme() {
  const context = React.useContext(TabsThemeContext);

  if (!context) {
    throw new Error("useTabsTheme must be used within a TabsProvider");
  }

  return context;
}

// Variants using `cva` for styling
const tabsListVariants = cva(
  "inline-flex h-9 items-center justify-center rounded-none bg-secondary text-muted-foreground",
  {
    variants: {
      variant: {
        default: "rounded-sm bg-secondary",
        underline: "h-11 justify-start gap-8 border-b bg-transparent",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

const tabsTriggerVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-sm py-1 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:text-foreground data-[state=active]:shadow",
  {
    variants: {
      variant: {
        default: "data-[state=active]:bg-accent px-3 h-full",
        underline:
          "h-11 rounded-none border-b-2 border-transparent data-[state=active]:border-primary-0",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

const Tabs = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root> & {
    variant?: TabsThemeContextType["variant"];
  }
>(({ variant = "default", ...props }, ref) => {
  return (
    <TabsThemeContext.Provider value={{ variant }}>
      <TabsPrimitive.Root ref={ref} {...props} />
    </TabsThemeContext.Provider>
  );
});
Tabs.displayName = TabsPrimitive.Root.displayName;

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> &
    VariantProps<typeof tabsListVariants>
>(({ className, ...props }, ref) => {
  const { variant } = useTabsTheme();

  return (
    <TabsPrimitive.List
      ref={ref}
      className={cn(tabsListVariants({ variant }), className)}
      {...props}
    />
  );
});
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> &
    VariantProps<typeof tabsTriggerVariants>
>(({ className, ...props }, ref) => {
  const { variant } = useTabsTheme();

  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(tabsTriggerVariants({ variant }), className)}
      {...props}
    />
  );
});
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
